import { MouseEvent, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ExpandableSection from '@components/animations/ExpandableSection';
import ActionIconButton from '@components/buttons/ActionIconButton';
import BasicCard from '@components/cards/BasicCard';
import DateStamp from '@components/dates/DateStamp';
import FileResource from '@components/FileResource';
import LegalDocumentForm from '@components/forms/LegalDocumentForm';
import LegalDocumentTracker from '@components/LegalDocumentTracker';
import Modal, { ModalElement } from '@components/Modal';
import Icon from '@components/text/Icon';
import Tracker from '@components/trackers/Tracker';
import styles from '@components/trackers/Tracker.module.css';
import TrackerPopover from '@components/trackers/TrackerPopover';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import useLazyQueryWithPromise from '@hooks/useLazyQueryWithPromise';
import useOpener from '@hooks/useOpener';
import {
  GetJourneyWithCareProfileQuery,
  GetJourneyWithDocumentsQuery,
  GetPdfDownloadDocument,
  GetPdfDownloadQuery,
  GetPdfDownloadQueryVariables,
  LegalDocumentFragment,
  LegalDocumentKindEnum,
  PdfTypeEnum,
  TrackerStatusEnum
} from '@typing/Generated';
import { downloadFile } from '@utils/downloadUtils';
import { sortByTimestamp } from '@utils/modelUtils';

type Props = {
  journey: GetJourneyWithCareProfileQuery['journey'] | GetJourneyWithDocumentsQuery['journey'];
};

type LegalDocumentProps = {
  handleEdit: (ld: LegalDocumentFragment, uploadOnly: boolean) => void;
  legalDocument: LegalDocumentFragment;
};

type LegalDocumentDescriptionProps = {
  description: string | ReactNode | null;
  labelKey: string;
  visible: boolean;
};

const LegalDocumentDescription = ({ description, labelKey, visible }: LegalDocumentDescriptionProps) => {
  if (!visible) return null;

  return (
    <div className={styles.description}>
      <div className={styles.maxContent + ' ' + styles.noWrap}>
        <BodyCopyBold color="primary700">
          <FormattedMessage id={labelKey} />
        </BodyCopyBold>
      </div>

      <BodyCopyBold color="neutral500">{description}</BodyCopyBold>
    </div>
  );
};

const LegalDocument = ({ handleEdit, legalDocument }: LegalDocumentProps) => {
  const opener = useOpener();
  const intl = useIntl();

  const displayDetailsToggle = useMemo(() => {
    if (legalDocument.notes || legalDocument.storageLocation || legalDocument.status || legalDocument.updateDate) {
      return true;
    }
    return false;
  }, [legalDocument]);

  return (
    <BasicCard boxShadow hover="none" padding="none">
      <div>
        <div className={styles.item}>
          <div className={styles.titleAndIcon}>
            <Icon
              ariaLabelKey="accessibility.ariaLabels.fileContract"
              className={styles.itemIcon}
              icon="fileContract"
            />

            <BodyCopyBold color="primary700">
              <FormattedMessage id={`pages.client.legalDocuments.titles.${legalDocument.kind}`} />
            </BodyCopyBold>
          </div>
          <div className={styles.actions}>
            {displayDetailsToggle && (
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.toggle"
                icon={opener.isOpen ? 'chevronUp' : 'chevronDown'}
                onClick={opener.toggle}
              />
            )}

            <TrackerPopover
              handleEdit={() => handleEdit(legalDocument, false)}
              handleUpload={() => handleEdit(legalDocument, true)}
            />
          </div>
        </div>
        <ExpandableSection isOpen={opener.isOpen} key={`legalDescriptions${legalDocument.id}`}>
          <div className={styles.descriptions}>
            <LegalDocumentDescription
              description={intl.formatMessage({ id: `models.client.legalDocument.${legalDocument.status}` })}
              labelKey="models.client.legalDocument.status"
              visible={!!legalDocument.status}
            />
            <LegalDocumentDescription
              description={legalDocument.storageLocation}
              labelKey="pages.legalDocuments.list.storageLocation"
              visible={!!legalDocument.storageLocation}
            />
            <LegalDocumentDescription
              description={<DateStamp value={legalDocument.updateDate} />}
              labelKey="pages.legalDocuments.list.updateDate"
              visible={!!legalDocument.updateDate}
            />
            <LegalDocumentDescription
              description={legalDocument.notes}
              labelKey="pages.legalDocuments.list.notes"
              visible={!!legalDocument.notes}
            />
            {legalDocument.fileResources.length > 0 && (
              <div className={styles.description}>
                <div className={styles.maxContent}>
                  <BodyCopyBold color="primary700">
                    <FormattedMessage id="pages.legalDocuments.list.attachments" />
                  </BodyCopyBold>
                </div>
                {legalDocument.fileResources.map(fr => (
                  <div key={fr.id}>
                    <FileResource fileResource={fr} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </ExpandableSection>
      </div>
    </BasicCard>
  );
};

const LegalDocuments = ({ journey }: Props) => {
  const intl = useIntl();
  const trackerOpener = useOpener();
  const addOpener = useOpener();
  const legalModalRef = useRef<ModalElement>(null);
  const [uploadOnly, setUploadOnly] = useState(false);
  const [editingLegalDocument, setEditingLegalDocument] = useState<LegalDocumentFragment | undefined>(undefined);

  const getPdfDownload = useLazyQueryWithPromise<GetPdfDownloadQuery, GetPdfDownloadQueryVariables>(
    GetPdfDownloadDocument
  );

  const handleDownload = useCallback(
    (event: MouseEvent<HTMLIonButtonElement>) => {
      event.preventDefault();
      getPdfDownload({
        fetchPolicy: 'no-cache',
        variables: { journeyId: journey.id, type: PdfTypeEnum.legal_tracker }
      }).then(async response => {
        downloadFile(response.data.pdfDownload.url, 'grayce-legal-documents.pdf');
      });
    },
    [getPdfDownload, journey.id]
  );

  return (
    <div className={styles.trackerContainer}>
      <Tracker
        canDownload={journey.legalDocuments.length > 0 && journey.legalTrackerStatus === TrackerStatusEnum.COMPLETED}
        handleDownload={handleDownload}
        kind="legal"
        opener={journey.legalTrackerStatus === TrackerStatusEnum.COMPLETED ? addOpener : trackerOpener}
        trackerStatus={journey.legalTrackerStatus}
      >
        <div className={styles.items}>
          {sortByTimestamp(journey.legalDocuments, lg => lg.createdAt).map(legalDocument => (
            <LegalDocument
              handleEdit={(ld, uploadOnly) => {
                setEditingLegalDocument(ld);
                setUploadOnly(uploadOnly);
                addOpener.open();
              }}
              key={legalDocument.id}
              legalDocument={legalDocument}
            />
          ))}
        </div>
      </Tracker>
      <LegalDocumentTracker journey={journey} legalDocumentsOpener={trackerOpener} />
      <Modal
        opener={addOpener}
        ref={legalModalRef}
        title={intl.formatMessage({ id: 'pages.client.legalDocuments.editLegalDocument' })}
      >
        {() => (
          <LegalDocumentForm
            defaultAsComplete
            journeyId={journey.id}
            kind={editingLegalDocument?.kind ?? LegalDocumentKindEnum.dpoa_finances}
            legalDocument={editingLegalDocument}
            onCancel={addOpener.close}
            onSuccess={addOpener.close}
            titleKey={`pages.client.legalDocuments.titles.${editingLegalDocument?.kind}`}
            uploadOnly={uploadOnly}
          />
        )}
      </Modal>
    </div>
  );
};

export default LegalDocuments;
